import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ItemPrice } from '@/api/type-response'
import { Volume } from '@/api/type-server'
import InfoCircleIcon from '@/assets/icons/info-circle.svg?react'
import WarningIcon from '@/assets/images/warning.svg?react'
import { Button, Loading, message, Modal, Radio, Trans } from '@/components'
import { ItemPriceType } from '@/consts/item'
import { ORDER_CATALOG } from '@/consts/order'
import { QuotaType } from '@/consts/team'
import { VOLUME_TYPE } from '@/models/volume'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal.tsx'
import WarningInfoBlock from '@/pages/space-mgr/components/WarningInfoBlock.tsx'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'
import { formatGB } from '@/utils/storage'
import { cn } from '@/utils/style'

const PAYMENT_TYPE = {
  1: 30,
  default: 30,
}

export type PlanAction = 'purchase' | 'renewal' | 'upgrade' | 'downgrade'

const formatPackagePrice = function (prices: ItemPrice[]) {
  if (!prices || !prices.length) return {}

  const originPrice = prices.find((p) => p.type === ItemPriceType.Original)
  const firstPrice = prices.find((p) => p.type === ItemPriceType.First)
  const renewPrice = prices.find((p) => p.type === ItemPriceType.Renew)
  const upgradePrice = prices.find((p) => p.type === ItemPriceType.Upgrade)
  const studentPrice = prices.find((p) => p.type === ItemPriceType.Student)
  const lastPrice = prices[prices.length - 1]

  return {
    originPrice,
    firstPrice,
    renewPrice,
    upgradePrice,
    studentPrice,
    lastPrice,
  }
}

interface ItemProps {
  data: {
    id: number
    placeholder?: boolean
    capacity: number
    currentPrice: number
    renewPrice: number
    originPrice: number
    period: string
    suggest?: string
  }
  currentId: number | null
  originalId: number | null
  onChange: (id: number) => void
  disabled: boolean
  disabledReason?: 'below-usage' | 'exceeds-quota'
}

function Item({ data, currentId, originalId, onChange, disabled, disabledReason }: ItemProps) {
  if (data.placeholder === true) {
    return <div />
  }

  const content = (
    <div
      onClick={() => onChange(data.id)}
      className={cn(
        'w-full px-4 py-3 rounded-2xl gap-3 justify-start border border-neutral-300 hover:bg-btn-secondary/5 hover:border-secondary focus:border-secondary focus:bg-btn-secondary/5 transition-colors duration-300 cursor-pointer',
        { 'opacity-50 cursor-not-allowed': disabled },
      )}
    >
      <div className="flex justify-between items-start">
        <div className=" font-bold text-[20px] leading-[28px] text-primary">
          {formatGB(data.capacity / 1024, { dynamicDecimals: true })}
        </div>
        <Radio checked={data.id === currentId} disabled={disabled} />
      </div>
      <div className="gap-2 flex">
        <div className=" t-sm text-primary">
          {formatPrice(data.id === originalId ? data.renewPrice : data.currentPrice)}
        </div>
        <div className=" t-sm text-tertiary">
          {data.period} <Trans i18nKey="space.expansionModal.days">Days</Trans>
        </div>
      </div>
    </div>
  )

  if (disabledReason === 'exceeds-quota') {
    return (
      <Tooltip
        title={
          <Trans i18nKey="space.expansionModal.quotaExceededTip">
            This plan exceeds your storage quota limit. Contact Glows Team to increase your quota.
          </Trans>
        }
      >
        {content}
      </Tooltip>
    )
  }

  return content
}

interface ConfirmPlanSummaryProps {
  action: PlanAction
  currentStorage: string
  selectedStorage: string
  currentExpirationDate: string
  newExpirationDate: string
  amountDue: number
}

function ConfirmPlanSummary({
  action,
  currentStorage,
  selectedStorage,
  currentExpirationDate,
  newExpirationDate,
  amountDue,
}: ConfirmPlanSummaryProps) {
  const { t } = useTranslation()
  const isDowngrade = action === 'downgrade'

  const actionLabel = {
    purchase: t('space.expansionModal.confirm.actionPurchase'),
    renewal: t('space.expansionModal.confirm.actionRenewal'),
    upgrade: t('space.expansionModal.confirm.actionUpgrade'),
    downgrade: t('space.expansionModal.confirm.actionDowngrade'),
  }[action]

  const alertText = {
    purchase: t('space.expansionModal.confirm.purchaseAlert', { storage: selectedStorage }),
    renewal: t('space.expansionModal.confirm.renewalAlert', { storage: selectedStorage }),
    upgrade: t('space.expansionModal.confirm.upgradeAlert', { storage: selectedStorage }),
    downgrade: t('space.expansionModal.confirm.downgradeAlert', { storage: selectedStorage }),
  }[action]

  const rows = [
    {
      key: 'currentStorage',
      label: t('space.expansionModal.confirm.currentStorage'),
      value: currentStorage,
    },
    {
      key: 'currentExpirationDate',
      label: t('space.expansionModal.confirm.currentExpirationDate'),
      value: currentExpirationDate,
    },
    {
      key: 'action',
      label: t('space.expansionModal.confirm.action'),
      value: actionLabel,
    },
    'divider',
    {
      key: 'selectedStorage',
      label: t('space.expansionModal.confirm.totalCapacityAfterPurchase'),
      value: selectedStorage,
    },
    {
      key: 'newExpirationDate',
      label: t('space.expansionModal.confirm.newExpirationDate'),
      value: newExpirationDate,
    },
    {
      key: 'amountDue',
      label:
        action === 'upgrade'
          ? t('space.expansionModal.confirm.amountDueProrated')
          : t('space.expansionModal.confirm.amountDue'),
      value: formatPrice(amountDue),
    },
  ] as const

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          'flex items-center gap-4 rounded-lg border p-4 t-sm',
          isDowngrade
            ? 'border-warning/40 bg-warning/10 text-warning'
            : 'border-highlight/30 bg-highlight/10 text-highlight',
        )}
      >
        {isDowngrade ? (
          <WarningIcon className="h-5 w-5 shrink-0" />
        ) : (
          <InfoCircleIcon className="h-5 w-5 shrink-0" />
        )}
        <p className="flex-1">{alertText}</p>
      </div>

      <div className="rounded-lg bg-neutral-50 p-4">
        <div className="mb-2 flex items-center gap-2">
          <span className="t-xs text-tertiary">
            <Trans i18nKey="space.expansionModal.confirm.planSummary">Plan Summary</Trans>
          </span>
          <div className="h-px flex-1 bg-neutral-200" />
        </div>
        <div className="flex flex-col gap-2">
          {rows.map((row) =>
            row === 'divider' ? (
              <div key="divider" className="my-1 h-px bg-neutral-200" />
            ) : (
              <div key={row.key} className="flex items-center justify-between gap-6">
                <div className="w-[220px] px-2 py-1 t-sm text-secondary">{row.label}</div>
                <div className="min-w-[172px] px-2 py-1 text-right t-sm text-primary">
                  {row.value}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  )
}

interface PlanConfirmModalProps extends ConfirmPlanSummaryProps {
  open: boolean
  loading: boolean
  onClose: () => void
  onConfirm: () => void
}

export function PlanConfirmModal({
  open,
  loading,
  action,
  currentStorage,
  selectedStorage,
  currentExpirationDate,
  newExpirationDate,
  amountDue,
  onClose,
  onConfirm,
}: PlanConfirmModalProps) {
  return (
    <Modal className="w-[640px]" open={open} onClose={onClose}>
      <Modal.Header>
        {action === 'purchase' ? (
          <Trans i18nKey="space.expansionModal.confirm.titlePurchase">Confirm Plan Purchase</Trans>
        ) : action === 'renewal' ? (
          <Trans i18nKey="space.expansionModal.confirm.titleRenewal">Confirm Plan Renewal</Trans>
        ) : action === 'upgrade' ? (
          <Trans i18nKey="space.expansionModal.confirm.titleUpgrade">Confirm Plan Upgrade</Trans>
        ) : (
          <Trans i18nKey="space.expansionModal.confirm.titleDowngrade">
            Confirm Plan Downgrade
          </Trans>
        )}
      </Modal.Header>
      <Modal.Body>
        <ConfirmPlanSummary
          action={action}
          currentStorage={currentStorage}
          selectedStorage={selectedStorage}
          currentExpirationDate={currentExpirationDate}
          newExpirationDate={newExpirationDate}
          amountDue={amountDue}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} disabled={loading}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type={action === 'downgrade' ? 'danger' : 'secondary'}
          disabled={loading}
          onClick={onConfirm}
        >
          {action === 'downgrade' ? (
            <Trans i18nKey="space.expansionModal.confirm.confirmDowngrade">Confirm Downgrade</Trans>
          ) : (
            <Trans i18nKey="space.expansionModal.confirm.confirmPay">Confirm &amp; Pay</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default function ExpansionModal(props: {
  open: boolean
  onClose: () => void
  onOk?: () => void
}) {
  const { open, onClose, onOk } = props
  const { t } = useTranslation()
  const [items, setItems] = useState([] as any)
  const [selectedItem, setSelectedItem] = useState([] as any)
  const [originalId, setOriginalId] = useState<any>()
  const [currentId, setCurentId] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<any>(undefined)
  const userStore = useUserStore()
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const [originalStorage, setOriginalStorage] = useState(0)
  const [autoRenewal, setAutoRenewal] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const selectedStorage = Number(selectedItem?.capacity || 0)
  const planAction: PlanAction = !order
    ? 'purchase'
    : originalId === selectedItem?.id
      ? 'renewal'
      : originalStorage < selectedStorage
        ? 'upgrade'
        : 'downgrade'
  const payableAmount =
    planAction === 'renewal'
      ? (selectedItem?.renewPrice ?? 0)
      : planAction === 'downgrade'
        ? 0
        : (selectedItem?.currentPrice ?? 0)
  const isNeedCharge = userStore.account.totalBonus.moneyCent < payableAmount

  const getDeadlineDate = () => {
    if (!order?.deadlineTime?.seconds) return null

    return dayjs(order.deadlineTime.seconds * 1000)
  }

  const getCurrentExpirationDate = () => getDeadlineDate()?.format('YYYY-MM-DD') ?? '-'

  const getNewExpirationDate = () => {
    const deadlineDate = getDeadlineDate()

    if (!order || !deadlineDate) return dayjs().add(30, 'day').format('YYYY-MM-DD')
    if (planAction === 'renewal') {
      return deadlineDate
        .add(selectedItem?.period || PAYMENT_TYPE.default, 'day')
        .format('YYYY-MM-DD')
    }

    return deadlineDate.format('YYYY-MM-DD')
  }

  const getButtonLabel = () => {
    if (isNeedCharge) {
      return <Trans i18nKey="space.chargeBtn">Recharge</Trans>
    }

    if (!order) {
      return <Trans i18nKey="space.expansionModal.checkoutBtn">Checkout</Trans>
    }

    if (planAction === 'renewal') {
      return <Trans i18nKey="space.expansionModal.checkoutBtn">Checkout</Trans> // Renew
    }

    return planAction === 'upgrade' ? (
      <Trans i18nKey="space.upgradePlanBtn">Upgrade Plan</Trans>
    ) : (
      <Trans i18nKey="space.downgradePlanBtn">Downgrade Plan</Trans>
    )
  }

  const handleButtonClick = () => {
    if (isNeedCharge) {
      setExchargeModalOpen(true)
      return
    }

    setConfirming(true)
  }

  useEffect(() => {
    if (!open) return
    setConfirming(false)

    const fetchData = async () => {
      // Parallel fetch: getVolumes and getOrders are independent
      const [volumesResponse, ordersResponse] = await Promise.all([
        API.getVolumes(),
        API.getOrders({ catalog: ORDER_CATALOG.STORAGE }),
      ])

      const fetchedVolumes = volumesResponse?.volumes || []
      const orders = ordersResponse?.orders || []
      const order = orders[orders.length - 1]
      setOrder(order)
      setAutoRenewal(order?.autoRenewal || false)
      setOriginalStorage(Number(order?.item?.desc?.storage?.capacityMB || 0))

      const extParams: any = {}
      if (order) {
        Object.assign(extParams, {
          from_order_id: order.id,
        })
      }

      const itemsResponse = await API.getItems({
        page: 1,
        per_page: 1000,
        catalog: ORDER_CATALOG.STORAGE,
        domain: 0,
        ...extParams,
      })

      // Calculate minimum capacity based on order expiration
      let minCapacityMB = 0
      if (userStore.isOrderExpired) {
        // Expired: non-image volumes' last_quota_gb + image volumes' usage_mb
        const nonImageQuotaMB = fetchedVolumes
          .filter((v: Volume) => v.type !== VOLUME_TYPE.IMAGE)
          .reduce((sum: number, v: Volume) => sum + (v.last_quota_gb || 0) * 1024, 0)

        const imageUsageMB = fetchedVolumes
          .filter((v: Volume) => v.type === VOLUME_TYPE.IMAGE)
          .reduce((sum: number, v: Volume) => sum + (v.usage_mb || 0), 0)

        minCapacityMB = nonImageQuotaMB + imageUsageMB
      } else {
        // Not expired: update userStore for other business logic
        await userStore.getUserInfo()
        minCapacityMB = userStore.partVolume?.usage_mb || 0
      }

      // Personal storage quota upper limit (type 5 = StorageBytes, value in bytes)
      const storageQuota = userStore.quotas.find((q) => q.type === QuotaType.StorageBytes)
      const maxCapacityMB = storageQuota ? storageQuota.value / (1024 * 1024) : 0

      const items = itemsResponse.items
        .map((item: any) => {
          const {
            originPrice,
            lastPrice: currentPrice,
            renewPrice,
          } = formatPackagePrice(item.prices || [])
          const capacityMB = item.desc.storage.capacityMB
          const exceedsQuota = maxCapacityMB > 0 && capacityMB > maxCapacityMB
          const belowUsage = capacityMB < minCapacityMB
          return {
            id: item.id,
            capacity: capacityMB,
            currentPrice: currentPrice?.priceCent,
            originPrice: originPrice?.priceCent,
            renewPrice: renewPrice?.priceCent,
            period: (PAYMENT_TYPE as any)[item.paymentType] || PAYMENT_TYPE.default,
            disabled: belowUsage || exceedsQuota,
            disabledReason: exceedsQuota ? 'exceeds-quota' : belowUsage ? 'below-usage' : undefined,
          }
        })
        .sort((prev: any, next: any) => prev.capacity - next.capacity)

      setItems(items)
      if (order) {
        const currentItem = items.find((item: any) => item.id === order.item.id)
        setOriginalId(currentItem?.id)
        setCurentId(currentItem?.id)
        setSelectedItem(currentItem)
      } else {
        setOriginalId(undefined)
        setCurentId(items?.[0]?.id)
        setSelectedItem(items?.[0])
      }
    }

    fetchData().catch(console.error)
  }, [open])

  const restoreExpiredVolumeQuotas = async () => {
    const volumesResponse = await API.getVolumes()
    const volumesToRestore = (volumesResponse?.volumes || []).filter(
      (volume: Volume) =>
        volume.id > 0 && volume.last_quota_gb > 0 && volume.type !== VOLUME_TYPE.IMAGE,
    )

    if (volumesToRestore.length === 0) return

    const results = await Promise.all(
      volumesToRestore.map(async (volume: Volume) => {
        try {
          const response = await API.modifySubvolume({
            id: volume.id,
            quota_gb: volume.last_quota_gb,
          })
          return { success: !response || response.code === 0, response }
        } catch (error) {
          return { success: false, error }
        }
      }),
    )

    if (results.some((result) => !result.success)) {
      throw new Error('Failed to restore volume quotas')
    }
  }

  const syncAutoRenewalAfterPlanChange = async () => {
    const planChanged = planAction === 'upgrade' || planAction === 'downgrade'
    if (!order || !planChanged || order.autoRenewal === autoRenewal) return true

    try {
      await API.updateOrderAutoRenewal({
        auto_renewal: autoRenewal,
        catalog: ORDER_CATALOG.STORAGE,
        type: 1,
      })
      return true
    } catch (error) {
      console.error('Failed to sync auto renewal after storage plan change:', error)
      return false
    }
  }

  const onOK = () => {
    if (currentId) {
      setLoading(true)
      const wasExpired = userStore.isOrderExpired
      const extParams: any = {}
      if (order) {
        Object.assign(extParams, { modify: order.item.id !== currentId }) // upgrade or renew
      }
      API.purchaseStoragePlan({
        item_id: currentId,
        auto_renewal: autoRenewal,
        ...extParams,
      })
        .then(async (res) => {
          if (res.code === 0) {
            const autoRenewalSynced = await syncAutoRenewalAfterPlanChange()

            if (wasExpired) {
              try {
                await restoreExpiredVolumeQuotas()
              } catch (error) {
                console.error('Failed to restore expired volume quotas:', error)
                message.show(
                  <Trans i18nKey="space.expansionModal.restoreQuotaFailed">
                    Subscription succeeded but failed to restore some volume quotas. Please use
                    Modify Quota to adjust manually.
                  </Trans>,
                )
                onClose()
                onOk?.()
                return
              }
            }

            if (autoRenewalSynced) {
              message.success(
                order
                  ? t('space.expansionModal.planModifiedSuccess')
                  : t('space.expansionModal.planPurchasedSuccess'),
              )
            } else {
              message.error(t('space.expansionModal.autoRenewalUpdateFailed'))
            }
            onClose()
            onOk?.()
          }
        })
        .catch((error) => {
          message.error(error.msg || t('space.expansionModal.planModificationFailed'))
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const handleClose = () => {
    if (!loading) {
      setConfirming(false)
      onClose()
    }
  }

  return (
    <>
      <Modal className="w-[800px]" open={open} onClose={handleClose}>
        <Modal.Header>
          <Trans i18nKey="space.expansionModal.title">Select Storage Plan</Trans>
        </Modal.Header>
        <Modal.Body>
          {items?.length > 0 ? (
            <>
              {selectedStorage !== originalStorage && (
                <WarningInfoBlock
                  _originalStorage={originalStorage}
                  _selectedItemStorage={selectedStorage}
                />
              )}
              <div className="max-h-[200px] flex-row flex-wrap justify-start gap-4 grid grid-cols-3 overflow-y-auto">
                {items.map((item: any) => {
                  return (
                    <Item
                      key={`item.${item.id}`}
                      data={item}
                      currentId={currentId}
                      originalId={originalId}
                      onChange={(id: any) => {
                        if (item.disabled) return
                        setCurentId(id)
                        setSelectedItem(item)
                      }}
                      disabled={item.disabled}
                      disabledReason={item.disabledReason}
                    ></Item>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="w-full flex items-center justify-center">
              <Loading />
            </div>
          )}

          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="space.expansionModal.autoRenewalTitle">Auto Renewal</Trans>
            </h3>

            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-3 rounded-lg border border-neutral-400 cursor-pointer">
                <Radio
                  checked={!autoRenewal}
                  onChange={() => setAutoRenewal(false)}
                  className="t-sm text-primary"
                >
                  <Trans i18nKey="space.expansionModal.autoRenewalNoLabel">No</Trans>
                </Radio>
              </div>
              <div className="flex-1 px-4 py-3 rounded-lg border border-neutral-400 cursor-pointer">
                <Radio
                  checked={autoRenewal}
                  onChange={() => setAutoRenewal(true)}
                  className="t-sm text-primary"
                >
                  <Trans i18nKey="space.expansionModal.autoRenewalYesLabel">Yes</Trans>
                </Radio>
              </div>
            </div>
          </div>

          <h3 className="text-primary mt-4 mb-2">
            <Trans i18nKey="space.expansionModal.summary">Summary</Trans>
          </h3>

          <table className="table">
            <tbody>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.expansionModal.storage">Storage</Trans>
                </td>
                <td className="text-primary t-sm !px-0 !py-3">
                  {selectedItem?.capacity
                    ? formatGB(selectedItem.capacity / 1024, { dynamicDecimals: true })
                    : '-'}
                </td>
              </tr>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.expansionModal.expire">Expires on</Trans>
                </td>
                <td className="text-primary t-sm !px-0 !py-3">
                  {!order
                    ? // purchase
                      dayjs().add(30, 'day').format('YYYY-MM-DD')
                    : originalId === selectedItem?.id
                      ? // renew
                        dayjs(order.deadlineTime.seconds * 1000)
                          .add(30, 'day')
                          .format('YYYY-MM-DD')
                      : // upgrade/downgrade
                        dayjs(order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')}
                </td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer className="justify-between">
          <div className="flex items-center space-x-2 ">
            <div className="h4 text-highlight">{formatPrice(payableAmount)}</div>
            <div className="rounded-full text-secondary py-2 px-3 bg-neutral-50 t-x-sb">
              <Trans i18nKey="space.expansionModal.myBalance">My Balance</Trans>
              <span className="text-primary ml-2">
                {formatPrice(userStore.account.totalBonus.moneyCent)}
              </span>
            </div>
          </div>
          <div className="space-x-4">
            <Button type="outline" onClick={handleClose}>
              <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
            </Button>

            <Button
              type="secondary"
              disabled={typeof currentId === 'undefined' || loading}
              onClick={handleButtonClick}
            >
              {getButtonLabel()}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <PlanConfirmModal
        open={confirming}
        loading={loading}
        action={planAction}
        currentStorage={
          originalStorage ? formatGB(originalStorage / 1024, { dynamicDecimals: true }) : '-'
        }
        selectedStorage={
          selectedItem?.capacity
            ? formatGB(selectedItem.capacity / 1024, { dynamicDecimals: true })
            : '-'
        }
        currentExpirationDate={getCurrentExpirationDate()}
        newExpirationDate={getNewExpirationDate()}
        amountDue={payableAmount}
        onClose={() => {
          if (!loading) {
            setConfirming(false)
          }
        }}
        onConfirm={onOK}
      />

      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getUserInfo()
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}
    </>
  )
}
